import { HanamiElement, BaseElement, createShadow, define, uniqueId } from '../core/base';

const css = `:host{display:block}.nav{position:relative;display:flex;gap:var(--hn-space-1);overflow-x:auto;border-bottom:1px solid var(--hn-color-border);scrollbar-width:none}.nav::-webkit-scrollbar{display:none}.indicator{position:absolute;left:0;bottom:-1px;width:0;height:2px;border-radius:var(--hn-radius-full);background:var(--hn-color-primary);transition:transform var(--hn-duration-slow) var(--hn-ease-bloom),width var(--hn-duration-slow) var(--hn-ease-bloom);pointer-events:none}.indicator.instant{transition:none}:host([variant="pills"]) .nav{display:inline-flex;max-width:100%;padding:0.25rem;border:0;border-radius:var(--hn-radius-full);background:var(--hn-color-bg-muted)}:host([variant="pills"]) .indicator{top:0.25rem;bottom:0.25rem;height:auto;background:var(--hn-color-surface-raised);box-shadow:var(--hn-shadow-sm)}:host([variant="pills"]) .nav{vertical-align:top}@media (prefers-reduced-motion:reduce){.indicator{transition:none}}`;

const html = /* html */ `
  <div class="nav" part="nav" role="tablist"><slot name="nav"></slot><span class="indicator" part="indicator"></span></div>
  <div class="panels" part="panels"><slot></slot></div>
`;

/** Un onglet : <hn-tab panel="nom">Libellé</hn-tab> */
export class HnTab extends BaseElement {
  static readonly tagName = 'hn-tab';
  static get observedAttributes() {
    return ['disabled'];
  }

  get panel(): string {
    return this.getAttribute('panel') ?? '';
  }
  set panel(value: string) {
    this.setAttribute('panel', value);
  }

  get disabled(): boolean {
    const v = this.getAttribute('disabled');
    return v !== null && v !== 'false';
  }
  set disabled(value: boolean) {
    this.toggleAttribute('disabled', Boolean(value));
  }

  connectedCallback(): void {
    this.setAttribute('role', 'tab');
    this.slot = 'nav';
    if (!this.id) this.id = uniqueId('hn-tab');
    this.attributeChangedCallback();
  }

  attributeChangedCallback(): void {
    if (this.disabled) this.setAttribute('aria-disabled', 'true');
    else this.removeAttribute('aria-disabled');
  }
}

/** Le contenu d'un onglet : <hn-tab-panel name="nom">…</hn-tab-panel> */
export class HnTabPanel extends BaseElement {
  static readonly tagName = 'hn-tab-panel';

  get name(): string {
    return this.getAttribute('name') ?? '';
  }
  set name(value: string) {
    this.setAttribute('name', value);
  }

  connectedCallback(): void {
    this.setAttribute('role', 'tabpanel');
    if (!this.id) this.id = uniqueId('hn-panel');
    if (!this.hasAttribute('tabindex')) this.tabIndex = 0;
  }
}

export interface TabsChangeDetail {
  value: string;
}

/**
 * Onglets accessibles (motif ARIA tablist) avec indicateur animé.
 *
 * Attributs : value (onglet actif), variant="line|pills", label, activation="auto|manual".
 * Événement : hn-change (detail.value).
 */
export class HnTabs extends HanamiElement {
  static readonly tagName = 'hn-tabs';
  static get observedAttributes() {
    return ['value', 'label', 'variant'];
  }

  private nav!: HTMLElement;
  private indicator!: HTMLElement;
  private resizeObserver?: ResizeObserver;
  private syncing = false;

  get value(): string {
    return this.getAttribute('value') ?? '';
  }
  set value(value: string) {
    this.setAttribute('value', value);
  }

  get tabs(): HnTab[] {
    return Array.from(this.querySelectorAll<HnTab>(':scope > hn-tab'));
  }

  get panels(): HnTabPanel[] {
    return Array.from(this.querySelectorAll<HnTabPanel>(':scope > hn-tab-panel'));
  }

  connectedCallback(): void {
    this.upgradeProperties('value');
    if (!this.shadowRoot) {
      const root = createShadow(this, css, html);
      this.nav = root.querySelector('.nav')!;
      this.indicator = root.querySelector('.indicator')!;
      root.querySelectorAll('slot').forEach(slot => slot.addEventListener('slotchange', () => this.sync()));
      this.nav.addEventListener('scroll', () => this.moveIndicator(true), { passive: true });
    }
    this.syncLabel();
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.moveIndicator(true));
      this.resizeObserver.observe(this.nav);
    }
    // Les enfants peuvent ne pas être encore définis (parsing HTML) : on attend.
    customElements.whenDefined('hn-tab').then(() => this.sync());
    this.sync();
  }

  disconnectedCallback(): void {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    this.resizeObserver?.disconnect();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (!this.nav || oldValue === newValue) return;
    if (name === 'value') this.sync();
    if (name === 'label') this.syncLabel();
    if (name === 'variant') this.moveIndicator(true);
  }

  /** Active un onglet par son nom de panneau. */
  select(value: string, { focus = false, emit = false } = {}): void {
    const tab = this.tabs.find(t => t.panel === value);
    if (!tab || tab.disabled) return;
    const changed = value !== this.value;
    if (changed) this.value = value;
    else this.sync();
    if (focus) tab.focus();
    if (changed && emit) this.emit<TabsChangeDetail>('hn-change', { value });
  }

  private syncLabel(): void {
    const label = this.getAttribute('label');
    if (label) this.nav.setAttribute('aria-label', label);
    else this.nav.removeAttribute('aria-label');
  }

  private sync(): void {
    if (this.syncing || !this.nav) return;
    this.syncing = true;
    const tabs = this.tabs;
    const panels = this.panels;
    let active = this.value;
    if (!tabs.some(t => t.panel === active && !t.disabled)) {
      active = tabs.find(t => !t.disabled)?.panel ?? '';
      if (active && active !== this.value) this.setAttribute('value', active);
    }
    for (const tab of tabs) {
      const selected = tab.panel === active;
      const panel = panels.find(p => p.name === tab.panel);
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      if (panel) {
        if (!panel.id) panel.id = uniqueId('hn-panel');
        if (!tab.id) tab.id = uniqueId('hn-tab');
        tab.setAttribute('aria-controls', panel.id);
        panel.setAttribute('aria-labelledby', tab.id);
      }
    }
    for (const panel of panels) panel.hidden = panel.name !== active;
    this.syncing = false;
    this.moveIndicator();
  }

  private moveIndicator(instant = false): void {
    const tab = this.tabs.find(t => t.getAttribute('aria-selected') === 'true');
    if (!tab || !this.indicator) {
      if (this.indicator) this.indicator.style.width = '0';
      return;
    }
    const navRect = this.nav.getBoundingClientRect();
    const rect = tab.getBoundingClientRect();
    if (!rect.width) return;
    this.indicator.classList.toggle('instant', instant);
    this.indicator.style.width = `${rect.width}px`;
    this.indicator.style.transform = `translateX(${rect.left - navRect.left + this.nav.scrollLeft}px)`;
    if (instant) requestAnimationFrame(() => this.indicator.classList.remove('instant'));
  }

  private onClick = (event: MouseEvent): void => {
    const tab = (event.target as Element).closest?.('hn-tab') as HnTab | null;
    if (tab && tab.parentElement === this && !tab.disabled) this.select(tab.panel, { emit: true });
  };

  private onKeydown = (event: KeyboardEvent): void => {
    const current = (event.target as Element).closest?.('hn-tab') as HnTab | null;
    if (!current || current.parentElement !== this) return;
    const tabs = this.tabs.filter(t => !t.disabled);
    const index = tabs.indexOf(current);
    let next: HnTab | undefined;
    switch (event.key) {
      case 'ArrowRight':
        next = tabs[(index + 1) % tabs.length];
        break;
      case 'ArrowLeft':
        next = tabs[(index - 1 + tabs.length) % tabs.length];
        break;
      case 'Home':
        next = tabs[0];
        break;
      case 'End':
        next = tabs[tabs.length - 1];
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.select(current.panel, { emit: true });
        return;
      default:
        return;
    }
    event.preventDefault();
    if (!next) return;
    if (this.getAttribute('activation') === 'manual') next.focus();
    else this.select(next.panel, { focus: true, emit: true });
  };
}

define(HnTab.tagName, HnTab);
define(HnTabPanel.tagName, HnTabPanel);
define(HnTabs.tagName, HnTabs);
